import CryptoJS from 'crypto-js';
import {
  recordApiBenchmark,
  recordBenchmarkError,
} from './benchmark.js';
import { APP_CONFIG } from './config.js';

const pendingRequests = new Map();

function encryptBody(value) {
  const key = CryptoJS.enc.Utf8.parse(APP_CONFIG.aesKey);
  const plaintext = CryptoJS.enc.Utf8.parse(JSON.stringify(value));
  const encrypted = CryptoJS.AES.encrypt(plaintext, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  });

  return encrypted.ciphertext.toString(CryptoJS.enc.Hex).toUpperCase();
}

function decryptBody(hex) {
  const key = CryptoJS.enc.Utf8.parse(APP_CONFIG.aesKey);
  const ciphertext = CryptoJS.enc.Hex.parse(hex.trim());
  const decrypted = CryptoJS.AES.decrypt(
    CryptoJS.lib.CipherParams.create({ ciphertext }),
    key,
    {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    },
  );

  return decrypted.toString(CryptoJS.enc.Utf8);
}

function requestKey(route, body) {
  return `${route}|${JSON.stringify(body)}`;
}

export function post(route, body) {
  const key = requestKey(route, body);
  const existing = pendingRequests.get(key);
  if (existing) return existing;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    APP_CONFIG.requestTimeoutMs,
  );
  const startedAt = performance.now();
  const encryptStartedAt = performance.now();
  const encryptedBody = encryptBody(body);
  const requestStartedAt = performance.now();

  const task = (async () => {
    try {
      const response = await fetch(`${APP_CONFIG.apiUrl}${route}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          KV: '1',
          V: APP_CONFIG.appVersion,
        },
        body: encryptedBody,
        signal: controller.signal,
        credentials: 'omit',
      });
      const headersAt = performance.now();
      const encrypted = await response.text();
      const downloadedAt = performance.now();
      const plaintext = decryptBody(encrypted);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${plaintext}`);
      }

      const parsed = JSON.parse(plaintext);
      const completedAt = performance.now();
      const message = parsed?.Msg ?? parsed?.msg ?? parsed?.error;
      if (message) {
        const code = parsed?.Code ?? parsed?.code;
        throw new Error(code ? `[${code}] ${message}` : String(message));
      }

      recordApiBenchmark({
        route,
        requestBody: body,
        startedAtMs: Math.round(startedAt * 10) / 10,
        status: response.status,
        encryptMs: Math.round((requestStartedAt - encryptStartedAt) * 10) / 10,
        ttfbMs: Math.round((headersAt - requestStartedAt) * 10) / 10,
        downloadMs: Math.round((downloadedAt - headersAt) * 10) / 10,
        decryptParseMs: Math.round((completedAt - downloadedAt) * 10) / 10,
        totalMs: Math.round((completedAt - startedAt) * 10) / 10,
        requestCipherChars: encryptedBody.length,
        responseCipherChars: encrypted.length,
        cipherBytes: Math.ceil(encrypted.length / 2),
        decodedBytes: new TextEncoder().encode(plaintext).length,
        wireBytes: Number(response.headers.get('content-length')) || null,
      });

      return parsed;
    } catch (error) {
      recordBenchmarkError(route, error);
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
      pendingRequests.delete(key);
    }
  })();

  pendingRequests.set(key, task);
  return task;
}

function vodListBody({ sectionOffset = 0, sectionLimit = 0 } = {}) {
  const cond = {
    ParentTypeID: APP_CONFIG.homeTypeId,
    Paging: {
      PageIndex: 0,
      PageSize: APP_CONFIG.homePageSize,
    },
  };

  if (APP_CONFIG.supportsSectionRange && sectionOffset > 0) {
    cond.SectionOffset = sectionOffset;
  }
  if (APP_CONFIG.supportsSectionRange && sectionLimit > 0) {
    cond.SectionLimit = sectionLimit;
  }

  return { Cond: cond };
}

export function loadHomePage() {
  const initialSectionRange = APP_CONFIG.supportsSectionRange
    ? { sectionLimit: APP_CONFIG.initialSectionCount }
    : {};

  return Promise.all([
    post('/u/main/category', {}),
    post('/u/category/detail', { TypeID: APP_CONFIG.homeTypeId }),
    post('/u/category/vod/list', vodListBody(initialSectionRange)),
    post('/u/vod/search/hotword', {}),
  ]).then(([categories, detail, vodList, hotwords]) => ({
    categories,
    detail,
    sections: vodList?.Vods ?? [],
    hotwords,
  }));
}

export function loadDeferredHomeSections() {
  if (!APP_CONFIG.supportsSectionRange) return Promise.resolve([]);

  return post(
    '/u/category/vod/list',
    vodListBody({ sectionOffset: APP_CONFIG.initialSectionCount }),
  ).then((vodList) => vodList?.Vods ?? []);
}
